import React, { useEffect, useMemo, useState } from "react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import { required } from "../../../../utils/formValidation";
import {
  MFA_ENFORCEMENT_OPTIONS,
  MFA_METHOD_SUGGESTIONS,
  MAX_PASSWORD_EXPIRY_DAYS,
} from "../../constants";
import { formatPasswordExpiryDate } from "../../utils/normalizeUserProfile";

const passwordExpiryDays =
  (message = `Enter days between 0 and ${MAX_PASSWORD_EXPIRY_DAYS} (max 7 digits).`) =>
  (value) => {
    if (value === undefined || value === null || String(value).trim() === "") {
      return "";
    }
    const days = Number(value);
    if (!Number.isFinite(days) || days < 0 || !Number.isInteger(days)) {
      return "Enter a whole number of days.";
    }
    if (days > MAX_PASSWORD_EXPIRY_DAYS) {
      return message;
    }
    return "";
  };

const schema = {
  profileName: [required("Profile name is required.")],
  passwordExpiryInDays: [
    required("Password expiry is required."),
    passwordExpiryDays(),
  ],
};

const isValidEnforcement = (value) =>
  MFA_ENFORCEMENT_OPTIONS.includes(String(value || "").trim());

/**
 * Shared create/edit form.
 * MFA enforcement must be one of: mandatory | optional | disabled (API).
 */
const UserProfileFormDialog = ({
  open,
  mode = "create",
  profile = null,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const isCreate = mode === "create";
  const [formError, setFormError] = useState("");
  const [enforcement, setEnforcement] = useState("disabled");
  const [methods, setMethods] = useState([]);

  const { values, getFieldProps, validateAll, reset, isValid, setFieldValue } =
    useFormValidation(
      {
        profileName: "",
        passwordExpiryInDays: "",
      },
      isCreate
        ? schema
        : { passwordExpiryInDays: schema.passwordExpiryInDays }
    );

  useEffect(() => {
    if (!open) return;
    setFormError("");
    const daysValue =
      profile?.passwordExpiryInDays != null
        ? profile.passwordExpiryInDays
        : profile?.passwordExpiryInSecs;
    const days =
      daysValue != null && Number.isFinite(Number(daysValue))
        ? String(Number(daysValue))
        : "";
    reset({
      profileName: profile?.profileName || "",
      passwordExpiryInDays: days,
    });

    const existing = String(
      profile?.mfaConfiguration?.enforcement || ""
    ).trim();
    setEnforcement(isValidEnforcement(existing) ? existing : "disabled");
    setMethods(
      Array.isArray(profile?.mfaConfiguration?.methods)
        ? [...profile.mfaConfiguration.methods]
        : []
    );
  }, [open, profile, reset]);

  const methodOptions = useMemo(
    () =>
      Array.from(
        new Set([...(MFA_METHOD_SUGGESTIONS || []), ...methods].filter(Boolean))
      ),
    [methods]
  );

  const expiryHelper = useMemo(() => {
    const raw = String(values.passwordExpiryInDays || "").trim();
    const days = Number(raw);
    if (!raw || !Number.isFinite(days) || days < 0) {
      return `Example: 365 (1 year). Max ${MAX_PASSWORD_EXPIRY_DAYS} days (7 digits).`;
    }
    if (days > MAX_PASSWORD_EXPIRY_DAYS) {
      return `Maximum is ${MAX_PASSWORD_EXPIRY_DAYS} days (7 digits).`;
    }
    return `Expires ${formatPasswordExpiryDate(days)} if the password were set today.`;
  }, [values.passwordExpiryInDays]);

  const canSubmit = Boolean(
    !loading &&
      isValid &&
      String(values.passwordExpiryInDays || "").trim() !== "" &&
      isValidEnforcement(enforcement) &&
      (isCreate ? String(values.profileName || "").trim() : true)
  );

  const handleSubmit = async () => {
    const { isValid: ok, errors } = validateAll();
    if (!ok) {
      setFormError(
        errors.profileName ||
          errors.passwordExpiryInDays ||
          "Please correct the highlighted fields."
      );
      return;
    }

    if (!isValidEnforcement(enforcement)) {
      setFormError(
        'MFA enforcement must be "mandatory", "optional", or "disabled".'
      );
      return;
    }

    const days = Number(values.passwordExpiryInDays);
    if (
      !Number.isFinite(days) ||
      days < 0 ||
      !Number.isInteger(days) ||
      days > MAX_PASSWORD_EXPIRY_DAYS
    ) {
      setFormError(
        `Enter password expiry between 0 and ${MAX_PASSWORD_EXPIRY_DAYS} days.`
      );
      return;
    }

    const payload = {
      profileName: isCreate
        ? values.profileName.trim()
        : profile?.profileName || values.profileName.trim(),
      passwordExpiryInSecs: days,
      passwordExpiryInDays: days,
      mfaConfiguration: {
        enforcement: String(enforcement).trim(),
        methods: methods.filter(Boolean).map(String),
      },
    };

    setFormError("");
    try {
      await onSubmit(payload);
    } catch (err) {
      setFormError(err?.message || "Unable to save user profile.");
    }
  };

  return (
    <FormDialog
      open={open}
      title={isCreate ? "Add User Profile" : "Edit User Profile"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      disableSubmit={!canSubmit}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel={isCreate ? "Create Profile" : "Save Changes"}
      loadingLabel="Saving..."
      maxWidth="sm"
    >
      <FormField
        label="Profile Name"
        required={isCreate}
        disabled={!isCreate || loading}
        helperText={
          isCreate
            ? "Unique profile type name (example: backgroundService)."
            : undefined
        }
        {...getFieldProps("profileName")}
        value={
          isCreate
            ? values.profileName
            : profile?.profileName || values.profileName || ""
        }
      />
      <FormField
        label="Password Expiry (days)"
        required
        type="text"
        disabled={loading}
        helperText={expiryHelper}
        inputProps={{
          inputMode: "numeric",
          maxLength: 7,
          pattern: "[0-9]*",
        }}
        {...getFieldProps("passwordExpiryInDays")}
        onChange={(event) => {
          const digits = String(event.target.value || "")
            .replace(/\D/g, "")
            .slice(0, 7);
          setFieldValue("passwordExpiryInDays", digits);
        }}
      />

      <Stack spacing={2}>
        <Typography variant="subtitle2" color="text.secondary">
          MFA Configuration
        </Typography>
        <FormControl fullWidth size="small" disabled={loading} required>
          <InputLabel id="mfa-enforcement-label">MFA Enforcement</InputLabel>
          <Select
            labelId="mfa-enforcement-label"
            id="mfa-enforcement-select"
            label="MFA Enforcement"
            value={
              isValidEnforcement(enforcement) ? enforcement : "disabled"
            }
            onChange={(event) => {
              setEnforcement(String(event.target.value || "disabled"));
            }}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                sx: {
                  maxHeight: 280,
                  mt: 0.5,
                },
              },
            }}
          >
            {MFA_ENFORCEMENT_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Allowed values: mandatory, optional, disabled.
          </FormHelperText>
        </FormControl>
        <ChipAutocomplete
          label="MFA Methods"
          freeSolo
          options={methodOptions}
          value={methods}
          onChange={setMethods}
          disabled={loading}
          helperText="Example: sms, authenticator_app."
        />
      </Stack>
    </FormDialog>
  );
};

export default UserProfileFormDialog;
